import { AnimationCallbackEvent } from '@angular/core';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import {
  BehaviorSubject,
  ReplaySubject,
  Subject,
  catchError,
  combineLatest,
  debounceTime,
  delay,
  distinctUntilChanged,
  filter,
  first,
  from,
  fromEvent,
  map,
  mapTo,
  of,
  pluck,
  retry,
  shareReplay,
  startWith,
  switchMap,
  takeUntil,
  tap,
  timer,
} from 'rxjs';

import { RxjsOperators } from 'ZalamoLibrary/src/lib/core/operators/RxjsOperators';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { HttpClient } from '@angular/common/http';
import { BlobStorageService } from 'ZalamoLibrary/src/lib/upload/azure/azure';
import { AliveState } from 'ZalamoLibrary/src/lib/core/ActiveState';
import {
  AvailableWallDecorScenesGQL,
  WallDecorPieceForDisplay,
  WallDecorPieceForDisplayType,
  WallDecorSceneForDisplay,
} from 'ZalamoLibrary/src/lib/ui/look-book-front/wall-decor-front/graphql';
import { ImagesContainer } from 'ZalamoLibrary/src/lib/upload/graphql';
import { Required } from 'ZalamoLibrary/src/lib/core/decorators/Required';
import { ZalamoDialogService } from 'ZalamoLibrary/src/lib/ui/zalamo-dialog/zalamo-dialog.service';
import { EditWallDecorPieceComponent } from 'ZalamoLibrary/src/lib/ui/look-book-front/wall-decor-front/edit-wall-decor-piece/edit-wall-decor-piece.component';
import { PhotoGroupList } from 'ZalamoLibrary/src/lib/ui/products/shopping-cart-back/actions/standalone';
import {
  getPhotoGroupFromPhotoId,
  getPhotoGroupIdFromPhotoId,
} from 'ZalamoLibrary/src/lib/ui/look-book-front/wall-decor-front/helpers';
import {
  ImagesInWallDecorMap,
  SingleImageInWallDecorMap,
} from 'ZalamoLibrary/src/lib/ui/look-book-front/wall-decor-front/types';
import { LookBookCanvasSceneRenderBody } from 'graphql/libs/look-book-rendering/types';
import { WallDecorRenderPreviewConfig } from '../../../../../../../photographer-client/src/app/modules/private-sessions/graphql';
import { groupArrayByIndex } from '../../../../../../../../helpers/array';
import { environment } from '../../enviroment/enviroment';
import {
  goToHoverElement,
  hideMessage,
  hideOverlay,
  openHover,
  showMessage,
} from '@ui/visual-guide/helpers';
import { FullscreenService } from '../../../fullscreen/fullscreen.service';
import { FaceDetectorService } from 'ZalamoLibrary/src/lib/zalamo/face-detection/face-detector.service';
import { maxSquareCalculator } from 'ZalamoLibrary/src/lib/zalamo/face-detection/calculateMaxSquare';

const cache = new Map<string, { url: SafeResourceUrl }>();
const layersCache = new Map<string, { overlayDom; overlayLayers }>();
const thumbsCache = new Map<string, { fileName: string; token: string }>();

@Component({
  selector: 'lib-wall-decor-front-scene',
  templateUrl: './wall-decor-front-scene.component.html',
  styleUrls: ['./wall-decor-front-scene.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class WallDecorFrontSceneComponent extends AliveState implements OnInit {
  animateColorPicker(event: AnimationCallbackEvent, entering: boolean): void {
    if (entering) this.hideElementEditIcons$.next(true);
    const duration = matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 1000;
    const positions = [{ transform: 'translateX(100%)' }, { transform: 'translateX(0)' }];
    const animation = event.target.animate(entering ? positions : positions.reverse(), { duration, easing: 'ease', fill: 'both' });
    animation.finished.catch(() => {}).finally(() => {
      if (!entering) this.hideElementEditIcons$.next(false);
      event.animationComplete();
    });
  }

  color = new UntypedFormControl(localStorage.wallColor ?? '#FFFFFF');
  currentIndex = 0;
  @Input() availableImages: string[] = [];
  @Input() limitPersonalization = false;
  @ViewChild('overlays', { static: true }) overlays: ElementRef<HTMLElement>;
  @ViewChild('controls', { static: true }) controls: ElementRef<HTMLElement>;
  @ViewChild('image', { static: true }) image: ElementRef<HTMLElement>;
  @ViewChild('container', { static: true }) container: ElementRef<HTMLElement>;
  @ViewChild('bg', { static: true }) bg: ElementRef<HTMLElement>;
  @Input() @Required imageRelations: PhotoGroupList[] = [];
  html$ = new BehaviorSubject(null);
  overlays$ = new BehaviorSubject(null);
  currentIndex$ = new BehaviorSubject('front');
  @ViewChild('viewer', { static: true }) viewer: ElementRef<HTMLElement>;
  calculateViewer$ = new Subject();
  @Input() context: ImagesContainer;
  @Input() contextId: ImagesContainer;
  @Output() colorPicked = new EventEmitter();
  currentColor$ = this.color.valueChanges.pipe(
    startWith(localStorage.wallColor ?? '#FFFFFF'),
    tap((color) => {
      console.log('UPDATEEEEED', color);
      console.log(
        this.elementRef.nativeElement.querySelectorAll('.width,.height'),
      );
    }),
  );
  pauseDomObserver = false;
  hideElementEditIcons$ = new BehaviorSubject(false);
  imageMap$ = new BehaviorSubject<ImagesInWallDecorMap>({});
  usedImagesList$ = this.imageMap$.pipe(
    map((v) => Object.values(v).map((v) => v.photoId)),
  );
  @Output() imagesUpdated = new EventEmitter<ImagesInWallDecorMap>();
  usedPhotos: { photoId: string; photoGroupId: string; ratio: number }[] = [];
  @Output() currentThumb = new EventEmitter<{
    fileName: string;
    token: string;
  }>();
  ratioToImageMap = new Map<
    number,
    {
      photoId: string;
      photoGroupId: string;
      crop?: {
        x: number;
        y: number;
        w: number;
        h: number;
      };
    }
  >();
  colors = [
    '#F6E7D8',
    '#EBBAB9',
    '#D1908F',
    '#652126',
    '#EAE0BC',
    '#B2C9AB',
    '#037171',
    '#26413C',
    '#C4E0F9',
    '#59A3B1',
    '#316A83',
    '#364156',
    '#FFFFFF',
    '#A3BAC3',
    '#56666B',
    '#000000',
  ];
  showColorPicker$ = new BehaviorSubject(false);
  @Output() showPreloader$ = new EventEmitter();
  preloaderVisible$ = this.showPreloader$.pipe(
    switchMap((show) => {
      if (show) {
        return timer(600).pipe(mapTo(true));
      }
      return of(false);
    }),
  );
  @HostBinding('class.rulersEnabled') toggleRulers = false;
  currentScene$ = new ReplaySubject<WallDecorSceneForDisplay>(1);
  currentSize = 0;
  sizeChanged = new Subject();
  currentRenderedSize = 0;
  currentOverlay$ = combineLatest([this.currentIndex$, this.overlays$]).pipe(
    RxjsOperators.domElementExists('.CssRenderer'),
    tap(([index, overlays]) => {
      this.pauseDomObserver = true;
      // @ts-ignore
      const targetNode = document.querySelector<HTMLElement>(
        '.CssRenderer',
      ) as HTMLElement;

      const editNode = document.querySelector(
        '.editWallDecorPiece',
      ) as HTMLElement;

      if (index === 'front' || Object.keys(overlays).indexOf(index) === -1) {
        // @ts-ignore
        targetNode.firstChild.style = overlays[index];

        editNode.style.display = 'none';
        targetNode.style.perspective = `${
          this.currentRenderedSize * (1475 / 1280)
        }px`;
      } else {
        // @ts-ignore
        targetNode.firstChild.style = overlays[index];
        targetNode.style.perspective = `${this.currentRenderedSize}px`;
        const selector = `.CssRenderer #${index}`;

        // setTimeout(() => {
        const pieceNode = document.querySelector(selector);
        if (pieceNode) {
          pieceNode.appendChild(editNode);
          editNode.style.display = 'block';
          const { top, right } = editNode.getBoundingClientRect();
          document
            .querySelector('lib-wall-decor-front-scene')
            .appendChild(editNode);
          editNode.style.top = `${Math.round(top + 35)}px`;
          editNode.style.left = `${Math.round(right + 25)}px`;
          editNode.style.position = `absolute`;
        }
        // }, 50);
      }
      if (this.limitPersonalization) {
        editNode.style.display = 'none';
      }
      this.pauseDomObserver = false;
    }),
  );
  translate$ = combineLatest([
    this.currentIndex$,
    this.overlays$.pipe(RxjsOperators.filterTruthy()),
  ]).pipe(
    map(
      ([v, overlays]) =>
        `translateY(${
          Math.max(0, Object.keys(overlays).indexOf(v)) *
          -this.currentRenderedSize
        }px)`,
    ),
    distinctUntilChanged(),
  );
  private _nodeId = null;
  hotImageMapUpdate = false;
  image$ = combineLatest([
    this.currentColor$
      .pipe(debounceTime(200))

      .pipe(
        tap((v) => this.colorPicked.next(v)),
        tap((color) => {
          console.log('COLOR UPDATED TO ', color);
          localStorage.wallColor = color;
        }),
      ),
    this.currentScene$.pipe(RxjsOperators.filterTruthy()),
    this.imageMap$.pipe(filter(() => !this.hotImageMapUpdate)),
    this.sizeChanged.pipe(startWith(false), debounceTime(200)),
  ]).pipe(
    tap((x) => {
      this.showPreloader$.next(true);
    }),
    switchMap(([currentColor, scene, imageMap]) => {
      const color = this.color.value;
      const index = JSON.stringify({ color, scene, imageMap });
      if (cache.has(index)) {
        console.log('USE INDEX');
        this.showPreloader$.next(false);
        this.applyLayers(layersCache.get(index));
        const { fileName, token } = thumbsCache.get(index);
        this.currentThumb.next({ fileName, token });

        return of(cache.get(index).url);
      }
      console.log('GET IMAGE');

      return this.getNewImage(color, scene, imageMap).pipe(
        catchError(() => {
          this.nodeId = null;
          return this.getNewImage(color, scene, imageMap);
        }),
        retry(10),
        tap(
          ({ ui: { overlayDom, overlayLayers, nodeId }, token, fileName }) => {
            layersCache.set(index, { overlayLayers, overlayDom });
            thumbsCache.set(index, { fileName, token });
            this.nodeId = nodeId;
            this.applyLayers({ overlayLayers, overlayDom });
            this.currentThumb.next({ fileName, token });
          },
        ),
        switchMap((c) =>
          from(
            this.blobStorageService
              .downloadFile(
                {
                  storageUri: 'https://newzalamomiddles.blob.core.windows.net',
                  storageAccessToken: c.token,
                  container: 'zalamo',
                  filename: null,
                },
                'zalamo',
                c.fileName,
              )
              .then((r) => r.blobBody),
          ),
        ),

        map((c) =>
          this.domSanitizer.bypassSecurityTrustResourceUrl(
            URL.createObjectURL(c),
          ),
        ),
        tap((url) => cache.set(index, { url })),
        tap(() => this.showPreloader$.next(false)),
      );
    }),
    shareReplay(1),
    tap(() => {
      this.currentScene$
        .pipe(RxjsOperators.filterTruthy())
        .pipe(
          tap((scene) => {
            const surfaces = this.overlays.nativeElement.querySelectorAll(
              '.CssRendererSurface',
            );
            const rulerColor = this.contrast(this.color.value);
            scene.pieces.forEach((piece, index) => {
              surfaces
                .item(index)
                .classList.toggle('tambour', piece.type === 'Tambour');
              const width = (piece.flip ? piece.height : piece.width) / 10;
              document.getElementById(
                piece.WallDecorPieceForDisplayId,
              ).innerHTML = `<div class="width ${rulerColor}" >${width} ${
                width > 15 ? 'cm' : ''
              }</div><div class="height  ${rulerColor}"><span>${
                (piece.flip ? piece.width : piece.height) / 10
              } cm</span>
              </div>`;
            });
          }),
          first(),
          switchMap(() =>
            timer(1000).pipe(
              filter(
                () => typeof sessionStorage.wallDecorTutorial === 'undefined',
              ),
              tap(() => {
                sessionStorage.wallDecorTutorial = 1;
                openHover(document.getElementById('wallDecorWallColorPicker'));
              }),
              delay(1000),
              switchMap(() =>
                showMessage(
                  this.zalamoDialogService,
                  [
                    {
                      type: 'text',
                      value: 'Tutaj możesz ustawić kolor ściany',
                    },
                  ],
                  document.getElementById('wallDecorWallColorPicker'),
                  {
                    hasPrevStep: false,
                    hasNextStep: false,
                  },
                ),
              ),
              delay(2000),
              switchMap(() => from(hideMessage())),
              delay(500),
              switchMap(() =>
                from(
                  goToHoverElement(
                    document.getElementById('RulersToggle'),
                  ).then(),
                ),
              ),
              delay(1000),
              switchMap(() =>
                from(
                  showMessage(
                    this.zalamoDialogService,
                    [
                      {
                        type: 'text',
                        value:
                          'Tutaj możesz włączyć linijkę aby poznać wymiary swojego WallDecor',
                      },
                    ],
                    document.getElementById('RulersToggle'),
                    {
                      hasPrevStep: false,
                      hasNextStep: false,
                    },
                  ),
                ),
              ),
              delay(2500),
              switchMap(() => from(hideMessage())),
              delay(500),
              switchMap(() =>
                from(
                  goToHoverElement(
                    document.querySelector('.CssRendererSurface'),
                  ).then(),
                ),
              ),
              switchMap(() =>
                from(
                  showMessage(
                    this.zalamoDialogService,
                    [
                      {
                        type: 'text',
                        value:
                          'Kliknij w WallDecor aby go edytować lub zobaczyć z bliska',
                      },
                    ],
                    document.querySelector('.CssRendererSurface'),
                    {
                      hasPrevStep: false,
                      hasNextStep: false,
                    },
                  ),
                ),
              ),

              delay(2500),
              switchMap(() => from(hideMessage())),
              switchMap(() => from(hideOverlay())),
              delay(500),
              map(() =>
                this.fullscreenService.open({
                  content:
                    'Pamiętaj, że tworząc swój projekt otrzymasz dokładnie taki produkt jaki zobaczysz na ekranie.',
                  data: { backdropClass: 'cdk-overlay-darker-backdrop' },
                }),
              ),
              delay(4000),
              tap((ref) => ref.close()),
            ),
          ),
        )
        .subscribe();
    }),
  );

  get nodeId(): any {
    return sessionStorage.getItem('wallDecorNodeId');
  }

  set nodeId(value: any) {
    if (!value) {
      sessionStorage.removeItem('wallDecorNodeId');
    } else {
      sessionStorage.setItem('wallDecorNodeId', value);
    }
  }

  scenes$ = this.availableWallDecorScenesGQL.fetch({}).pipe(
    pluck('data', 'getAvailableWallDecorScenes'),
    map((scenes) => {
      return [...scenes].sort((a, b) => a.pieces.length - b.pieces.length);
    }),
  );

  @Input() set scene(scene: WallDecorSceneForDisplay) {
    this.currentScene$.next(scene);
  }

  @Input() set preset(scene: WallDecorRenderPreviewConfig) {
    console.log('SCENE FROM PRECONFIG', scene);
    if (!scene) {
      return;
    }
    if (scene.color) {
      this.color.patchValue(scene.color);
    }
    this.scenes$
      .pipe(
        tap((scenes: WallDecorSceneForDisplay[]) => {
          const matchingScene = scenes.find(
            ({ wallDecorSceneForDisplayId }) =>
              wallDecorSceneForDisplayId === scene.wallDecorSceneId,
          );
          this.currentScene$.next(matchingScene);
          const photos = this.imageRelations.reduce(
            (previousValue, currentValue) => {
              return {
                ...previousValue,
                ...groupArrayByIndex(currentValue.photos, 'photoId'),
              };
            },
            {},
          );
          if (typeof matchingScene === 'undefined') {
            return null;
          }
          const indexedPieces = groupArrayByIndex(
            matchingScene.pieces,
            'WallDecorPieceForDisplayId',
          );
          // @ts-ignore
          const imageMap = Object.fromEntries(
            Object.entries(scene.images).map(([index, value]) => {
              const imageRatio =
                photos[value.photoId].width / photos[value.photoId].height;

              const currentValue = indexedPieces[value.wallDecorPieceId]
                ? indexedPieces[value.wallDecorPieceId]
                : indexedPieces[index];
              const containerRatio = currentValue.flip
                ? currentValue.imageHeight / currentValue.imageWidth
                : currentValue.imageWidth / currentValue.imageHeight;

              return [
                value.wallDecorPieceId ? value.wallDecorPieceId : index,
                {
                  ...value,
                  photoGroupId: photos[value.photoId].photoGroupId,
                },
              ];
            }),
          );

          Object.values(imageMap).forEach(({ photoId }) => {
            if (
              this.usedPhotos.findIndex(
                (photo) => photo.photoId === photoId,
              ) === -1
            ) {
              const details = getPhotoGroupFromPhotoId(
                photoId,
                this.imageRelations,
              );
              this.usedPhotos.push({
                photoId,
                photoGroupId: getPhotoGroupIdFromPhotoId(
                  photoId,
                  this.imageRelations,
                ),
                ratio: details.width / details.height,
              });
            }
          });

          this.imageMap$.next(
            // @ts-ignore
            imageMap,
          );
        }),
        first(),
      )
      .subscribe((scenes) => {});
  }

  @HostBinding('class.isFront') get isFront() {
    return this.currentIndex$.value === 'front';
  }

  constructor(
    private domSanitizer: DomSanitizer,
    private httpClient: HttpClient,
    private blobStorageService: BlobStorageService,
    private zalamoDialogService: ZalamoDialogService,
    private elementRef: ElementRef,
    private availableWallDecorScenesGQL: AvailableWallDecorScenesGQL,
    private fullscreenService: FullscreenService,
    private faceDetectorService: FaceDetectorService,
  ) {
    super();
  }

  applyLayers({ overlayLayers, overlayDom }: { overlayLayers; overlayDom }) {
    this.pauseDomObserver = true;
    this.overlays.nativeElement.innerHTML = overlayDom;
    if (!Object.keys(overlayLayers).includes(this.currentIndex$.value)) {
      this.currentIndex$.next('front');
    }
    this.image.nativeElement.style.transform = `
              }translateY(${
                Math.max(
                  0,
                  Object.keys(overlayLayers).indexOf(this.currentIndex$.value),
                ) * -this.currentRenderedSize
              }px)`;

    this.overlays$.next(overlayLayers);
    this.pauseDomObserver = false;
  }

  getPieceRatioForMap({ width, height }: WallDecorPieceForDisplay) {
    return (width / height).toFixed(2);
  }

  getNewImage(
    color,
    scene: WallDecorSceneForDisplay,
    imageMap: ImagesInWallDecorMap,
  ) {
    return (
      Object.values(imageMap).length
        ? combineLatest(
            Object.values(imageMap)
              .map((p) => p.photoId)
              .map((photoId) =>
                this.faceDetectorService
                  .detectFacesByPhotoId(photoId)
                  .pipe(map((f) => ({ photoId, ...f }))),
              ),
          )
        : of([])
    ).pipe(
      switchMap((faces) => {
        // @ts-ignore
        const facesIndexed = Object.fromEntries(
          faces.map((f) => [f.photoId, f]),
        );
        const body: LookBookCanvasSceneRenderBody = {
          color,
          sceneId: scene.wallDecorSceneForDisplayId,
          nodeId: this.nodeId,
          images: scene.pieces.reduce((prevValue, currentValue) => {
            console.log(
              'CURRENT RATIO',
              this.getPieceRatioForMap(currentValue),
            );
            if (imageMap[currentValue.WallDecorPieceForDisplayId]) {
              prevValue[currentValue.WallDecorPieceForDisplayId] =
                imageMap[currentValue.WallDecorPieceForDisplayId];
            } else {
              const possibleImages = this.usedPhotos.filter(
                ({ ratio, photoId }) =>
                  // Math.floor(ratio) ===
                  // Math.floor(
                  //   currentValue.flip
                  //     ? currentValue.imageHeight / currentValue.imageWidth
                  //     : currentValue.imageWidth / currentValue.imageHeight
                  // ) ||
                  // currentValue.type === WallDecorPieceForDisplayType.Tambour &&
                  !scene.pieces
                    .map(
                      ({ WallDecorPieceForDisplayId }) =>
                        imageMap[WallDecorPieceForDisplayId]?.photoId,
                    )
                    .includes(photoId) &&
                  !Object.values(prevValue)
                    .map((p) => p.photoId)
                    .includes(photoId),
              );
              if (possibleImages.length) {
                const imageRatio = possibleImages[0].ratio;
                const containerRatio = currentValue.flip
                  ? currentValue.imageHeight / currentValue.imageWidth
                  : currentValue.imageWidth / currentValue.imageHeight;
                const gravityCenter = maxSquareCalculator(
                  facesIndexed[possibleImages[0].photoId]
                    ? facesIndexed[possibleImages[0].photoId].faces
                    : [],
                  facesIndexed[possibleImages[0].photoId]?.gravityCenter,
                  imageRatio,
                  containerRatio,
                );
                prevValue[currentValue.WallDecorPieceForDisplayId] = {
                  ...possibleImages[0],
                  isFlip: currentValue.flip,
                  crop: gravityCenter
                    ? gravityCenter
                    : containerRatio > imageRatio
                      ? {
                          w: 100,
                          x: 0,
                          h: (imageRatio / containerRatio) * 100,
                          y: (100 - (imageRatio / containerRatio) * 100) / 2,
                        }
                      : {
                          h: 100,
                          y: 0,
                          w: (containerRatio / imageRatio) * 100,
                          x: (100 - (containerRatio / imageRatio) * 100) / 2,
                        },
                };
                this.hotImageMapUpdate = true;
                const temp: Record<string, SingleImageInWallDecorMap> = {};
                temp[currentValue.WallDecorPieceForDisplayId] =
                  prevValue[currentValue.WallDecorPieceForDisplayId];
                this.imageMap$.next({
                  ...this.imageMap$.value,
                  ...temp,
                });
                this.hotImageMapUpdate = false;
              }
              // if (
              //   this.ratioToImageMap.has(this.getPieceRatioForMap(currentValue))
              // ) {
              //   prevValue[currentValue.WallDecorPieceForDisplayId] =
              //     this.ratioToImageMap.get(this.getPieceRatioForMap(currentValue));
              // }
            }

            return prevValue;
          }, {} as ImagesInWallDecorMap),
          size: this.currentRenderedSize,
          pieces: [
            'front',
            ...scene.pieces.map(
              ({ WallDecorPieceForDisplayId }) => WallDecorPieceForDisplayId,
            ),
          ],
        };
        return this.httpClient.post<{
          fileName: string;
          token: string;
          ui: { overlayDom; overlayLayers; nodeId: string };
        }>(environment.canvasScenesUrl, body, { responseType: 'json' });
      }),
    );
  }

  renderedImageSize(targetSize) {
    const fresh = Math.min(
      1280,
      Math.ceil((targetSize * window.devicePixelRatio) / 80) * 80,
    );
    if (fresh !== this.currentRenderedSize) {
      this.currentRenderedSize = fresh;
      this.sizeChanged.next(true);
    }
    this.currentRenderedSize = fresh;
    this.viewer.nativeElement.style.width = `${this.currentRenderedSize}px`;
    this.viewer.nativeElement.style.height = `${this.currentRenderedSize}px`;

    return this.currentRenderedSize;
  }

  ngOnInit(): void {
    this.subscribeWhileAlive(
      this.imageMap$.pipe(tap((v) => this.imagesUpdated.next(v))),
      this.currentOverlay$,
      fromEvent(window, 'resize').pipe(
        tap(() => this.calculateViewer$.next(undefined)),
      ),
      this.calculateViewer$.pipe(
        tap(() => {
          this.container.nativeElement.style.width = '100%';
          this.container.nativeElement.style.height = '100%';
          this.container.nativeElement.style.width = '1280px';
          this.container.nativeElement.style.height = '1280px';
          const containerRect =
            this.elementRef.nativeElement.getBoundingClientRect();
          this.container.nativeElement.style.width = 'auto';
          this.container.nativeElement.style.height = 'auto';
          console.log(containerRect);
          const targetSize = Math.min(
            this.renderedImageSize(
              Math.min(containerRect.width, containerRect.height),
            ),
            Math.min(containerRect.width, containerRect.height),
          );
          const targetScale = targetSize / this.currentRenderedSize;
          console.log(targetScale);
          this.viewer.nativeElement.style.transform = `scale(${targetScale})`;
          const freshContainerRect =
            this.elementRef.nativeElement.getBoundingClientRect();
          this.currentSize = this.currentRenderedSize * targetScale;
          this.controls.nativeElement.style.width = `${
            this.currentRenderedSize * targetScale
          }px`;
          this.controls.nativeElement.style.height = `${
            this.currentRenderedSize * targetScale
          }px`;

          this.container.nativeElement.style.width = `${
            this.currentRenderedSize * targetScale
          }px`;
          this.container.nativeElement.style.height = `${
            this.currentRenderedSize * targetScale
          }px`;
          this.bg.nativeElement.style.width = `${
            this.currentRenderedSize * targetScale
          }px`;
          this.bg.nativeElement.style.height = `${
            this.currentRenderedSize * targetScale
          }px`;
          this.bg.nativeElement.style.top = `${
            this.elementRef.nativeElement.getBoundingClientRect().top
          }px`;
        }),
      ),
    );

    console.log(document.body.clientHeight);
    this.calculateViewer$.next(undefined);
    const elementToObserve = document.querySelector('.overlays');

    // create a new instance of `MutationObserver` named `observer`,
    // passing it a callback function
    const observer = new MutationObserver((a) => {
      if (!this.pauseDomObserver) {
        // this.currentIndex$.next(this.currentIndex$.value);
      }
    });

    // call `observe()` on that MutationObserver instance,
    // passing it the element to observe, and the options object
    // observer.observe(elementToObserve, {
    //   subtree: true,
    //   childList: true,
    //   attributes: true,
    // });
  }

  overlayClicked(event: { target: HTMLElement }) {
    this.showColorPicker$.next(false);
    if (event.target.id === '') {
      this.currentIndex$.next('front');
    }
    if (this.overlays$.value[event.target.id]) {
      if (this.currentIndex$.value === event.target.id) {
        this.openPhotoEditor();
      }
      this.currentIndex$.next(event.target.id);
    }
  }

  openPhotoEditor() {
    if (this.limitPersonalization) {
      return;
    }
    combineLatest([this.currentIndex$, this.currentScene$])
      .pipe(
        tap(([currentIndex, scene]) => {
          const ref = this.zalamoDialogService.openRight(
            EditWallDecorPieceComponent,
          );
          ref.componentInstance.availableImages = [
            ...new Set([
              ...this.availableImages,
              ...Object.values(this.imageMap$.value)
                .filter((v) => v.photoId)
                .map(({ photoId }) => photoId),
            ]),
          ];
          ref.componentInstance.imageRelations = this.imageRelations;
          ref.componentInstance.previous = {
            crop: this.imageMap$.value[currentIndex]?.crop,
            photoId: this.imageMap$.value[currentIndex]?.photoId,
          };

          ref.componentInstance.otherImages = this.imageMap$.value;
          for (const i in ref.componentInstance.otherImages) {
            if (!scene.pieces.some((p) => p.WallDecorPieceForDisplayId === i)) {
              delete ref.componentInstance.otherImages[i];
            }
          }
          console.log(this.currentScene$);
          const currentPiece = scene.pieces.find(
            ({ WallDecorPieceForDisplayId }) =>
              WallDecorPieceForDisplayId === currentIndex,
          );
          ref.componentInstance.ratio = currentPiece.flip
            ? currentPiece.imageHeight / currentPiece.imageWidth
            : currentPiece.imageWidth / currentPiece.imageHeight;
          ref.componentInstance.rounding =
            currentPiece.type === WallDecorPieceForDisplayType.Tambour ? 50 : 0;
          ref.componentInstance.saved
            .pipe(
              tap(({ photoId, crop, isFlip }) => {
                const temp: Record<
                  string,
                  {
                    photoId: string;
                    photoGroupId: string;
                    isFlip: boolean;
                    crop: {
                      x: number;
                      y: number;
                      w: number;
                      h: number;
                    };
                  }
                > = {};
                temp[this.currentIndex$.value] = {
                  photoId,
                  photoGroupId: getPhotoGroupIdFromPhotoId(
                    photoId,
                    this.imageRelations,
                  ),
                  crop,
                  isFlip: currentPiece.flip,
                };
                this.imageMap$.next({
                  ...this.imageMap$.value,
                  ...temp,
                });
                if (
                  this.usedPhotos.findIndex(
                    (photo) => photo.photoId === photoId,
                  ) === -1
                ) {
                  const details = getPhotoGroupFromPhotoId(
                    photoId,
                    this.imageRelations,
                  );
                  if (details) {
                    this.usedPhotos.push({
                      photoId,
                      photoGroupId: getPhotoGroupIdFromPhotoId(
                        photoId,
                        this.imageRelations,
                      ),
                      ratio: details.width / details.height,
                    });
                  }
                }

                ref.close();
              }),
              takeUntil(ref.afterClosed()),
            )
            .subscribe();
        }),
        first(),
      )
      .subscribe();
  }

  contrast(hex) {
    // Strip everything except the integers eg. "rgb(" and ")" and " "
    const rgb = hex
      .replace(
        /^#?([a-f\d])([a-f\d])([a-f\d])$/i,
        (m, r, g, b) => '#' + r + r + g + g + b + b,
      )
      .substring(1)
      .match(/.{2}/g)
      .map((x) => parseInt(x, 16));

    // map RGB values to variables
    const r = rgb[0];
    const g = rgb[1];
    const b = rgb[2];

    // calculate contrast of color (standard grayscale algorithmic formula)
    const contrast =
      (Math.round(r * 299) + Math.round(g * 587) + Math.round(b * 114)) / 1000;

    return contrast >= 128 ? 'black' : 'white';
  }
}
